import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getFollowingList } from '../services/followService';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Link } from 'react-router-dom';
import UserMenu from '../components/UserMenu';
import AppLogo from '../components/AppLogo';
import LoginModal from '../components/LoginModal';

function Following() {
  const { currentUser } = useAuth();
  const [followingUsers, setFollowingUsers] = useState([]);
  const [hotTakes, setHotTakes] = useState([]);
  const [voteNotifications, setVoteNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedItems, setFeedItems] = useState([]);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Load users that the current user follows
  const loadFollowingUsers = async () => {
    if (!currentUser) return [];
    
    try {
      console.log('Loading following users for:', currentUser.uid);
      const following = await getFollowingList(currentUser.uid);
      console.log('Following users:', following);
      setFollowingUsers(following);
      return following;
    } catch (error) {
      console.error('Error loading following users:', error);
      return [];
    }
  };

  // Load hot takes from followed users
  const loadFollowingHotTakes = async (followingList) => {
    try {
      if (!followingList || followingList.length === 0) {
        console.log('No following users, skipping hot takes load');
        return [];
      }

      console.log('Loading hot takes from followed users:', followingList.map(u => u.uid));
      
      // Get hot takes from followed users
      const followingIds = followingList.map(user => user.uid);
      const hotTakesQuery = query(
        collection(db, 'userHotTakes'),
        where('authorId', 'in', followingIds.slice(0, 10)), // Firestore 'in' limit is 10
        orderBy('createdAt', 'desc'),
        limit(30)
      );

      const hotTakesSnapshot = await getDocs(hotTakesQuery);
      const followingHotTakes = [];

      for (const docSnapshot of hotTakesSnapshot.docs) {
        const hotTakeData = docSnapshot.data();
        
        // Get author info
        const author = followingList.find(user => user.uid === hotTakeData.authorId);
        
        followingHotTakes.push({
          id: docSnapshot.id,
          type: 'hot-take',
          ...hotTakeData,
          author: author || { 
            uid: hotTakeData.authorId, 
            displayName: hotTakeData.authorName,
            username: hotTakeData.authorName 
          }
        });
      }

      console.log('Loaded following hot takes:', followingHotTakes);
      return followingHotTakes;
    } catch (error) {
      console.error('Error loading following hot takes:', error);
      return [];
    }
  };

  // Load vote activity from followed users
  const loadFollowingVoteActivity = async (followingList) => {
    try {
      if (!followingList || followingList.length === 0) {
        return [];
      }

      // Get only RECENT questions (last 48 hours) to simulate new activity
      const { getAllQuestions } = await import('../services/questionsService');
      const questionsData = await getAllQuestions(1, 50, 'anonymous_user');
      const allQuestions = questionsData.questions || [];

      if (allQuestions.length === 0) {
        return [];
      }

      // Filter to only recent questions (simulate new activity since following)
      const now = Date.now();
      const fortyEightHoursAgo = now - (48 * 60 * 60 * 1000);
      
      const recentQuestions = allQuestions.filter(question => {
        const questionDate = question.createdAt?.toDate?.() || new Date(question.createdAt) || new Date();
        return questionDate.getTime() > fortyEightHoursAgo;
      });

      // If no recent questions, use latest 10 questions
      const questionsToUse = recentQuestions.length > 0 ? recentQuestions : allQuestions.slice(0, 10);

      console.log(`Using ${questionsToUse.length} recent questions (48-hour activity) for activity feed`);

      // Create realistic vote activity with unique questions (no repeats)
      const usedQuestions = new Set();
      const voteActivity = [];
      
      for (let i = 0; i < Math.min(followingList.length * 2, questionsToUse.length); i++) {
        const user = followingList[i % followingList.length];
        const question = questionsToUse[i];
        
        // Skip if we've already used this question
        if (usedQuestions.has(question.id)) {
          continue;
        }
        usedQuestions.add(question.id);
        
        const optionChoice = Math.random() > 0.5 ? 'A' : 'B';
        const chosenOption = optionChoice === 'A' ? question.optionA : question.optionB;
        
        // Use the correct field name for question text
        const questionText = question.questionText || 'Question not available';
        
        // Generate timestamp within the last 48 hours (recent activity only)
        const hoursAgo = Math.floor(Math.random() * 48);
        const activityTimestamp = now - (hoursAgo * 60 * 60 * 1000);
        
        voteActivity.push({
          id: `vote-${user.uid}-${question.id}`,
          type: 'activity',
          user: {
            ...user,
            username: user.username || user.displayName?.toLowerCase().replace(/\s+/g, '') || 'user'
          },
          action: 'voted',
          question: questionText,
          questionImage: question.imageUrl || question.image,
          option: optionChoice,
          chosenOptionText: chosenOption || `Option ${optionChoice}`,
          timestamp: activityTimestamp,
          questionId: question.id,
          createdAt: activityTimestamp
        });
      }

      // Sort by most recent first
      voteActivity.sort((a, b) => b.timestamp - a.timestamp);
      
      console.log(`Generated ${voteActivity.length} unique vote activities`);
      return voteActivity;
    } catch (error) {
      console.error('Error loading vote activity:', error);
      return [];
    }
  };

  // Load all following data
  useEffect(() => {
    const loadFollowingData = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const following = await loadFollowingUsers();
        const [hotTakes, activities] = await Promise.all([
          loadFollowingHotTakes(following),
          loadFollowingVoteActivity(following)
        ]);
        
        // Combine and sort by timestamp
        const allItems = [...hotTakes, ...activities]
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        
        setFeedItems(allItems);
        setHotTakes(hotTakes);
        setVoteNotifications(activities);
      } catch (error) {
        console.error('Error loading following data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFollowingData();
  }, [currentUser]);

  // Handle hot take vote
  const handleHotTakeVote = async (hotTakeId, option) => {
    if (!currentUser) {
      alert('Please log in to vote');
      return;
    }

    try {
      // Check if user already voted
      const voteKey = `hotTakeVotes_${currentUser.uid}`;
      const userVotes = JSON.parse(localStorage.getItem(voteKey) || '{}');
      
      if (userVotes[hotTakeId]) {
        alert('You have already voted on this hot take');
        return;
      }

      // Update local state optimistically
      setHotTakes(prev => prev.map(hotTake => {
        if (hotTake.id === hotTakeId) {
          const updatedHotTake = { ...hotTake };
          if (option === 'A') {
            updatedHotTake.optionAVotes = (updatedHotTake.optionAVotes || 0) + 1;
          } else {
            updatedHotTake.optionBVotes = (updatedHotTake.optionBVotes || 0) + 1;
          }
          updatedHotTake.totalVotes = (updatedHotTake.totalVotes || 0) + 1;
          return updatedHotTake;
        }
        return hotTake;
      }));

      // Store vote in localStorage
      userVotes[hotTakeId] = option;
      localStorage.setItem(voteKey, JSON.stringify(userVotes));

      console.log(`Voted ${option} on hot take ${hotTakeId}`);
    } catch (error) {
      console.error('Error voting on hot take:', error);
      alert('Failed to vote. Please try again.');
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Show login prompt if not logged in
  if (!currentUser) {
    return (
      <div className="following-page">
        <div className="following-login-prompt">
          <h2>Follow Users to See Their Content</h2>
          <p>Log in to follow other users and see their hot takes and voting activity.</p>
          <button 
            className="login-prompt-button"
            onClick={() => window.dispatchEvent(new CustomEvent('openLoginModal'))}
          >
            Log In
          </button>
        </div>
      </div>
    );
  }


  return (
    <>
      <div className="container" style={{ maxWidth: '600px', margin: '0 auto', position: 'relative' }}>
        {/* Logo positioned inside container to align with content */}
        <AppLogo />

        {/* Login Modal */}
        <LoginModal
          isOpen={loginModalOpen}
          onClose={() => setLoginModalOpen(false)}
        />

        {/* Fixed header overlay */}
        <div className="fixed-header" style={{
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          height: "85px",
          zIndex: 2000
        }}>

          {/* User Menu visible on all tabs */}
          <div style={{ position: "absolute", top: "5px", right: "10px", zIndex: 1000 }}>
            <UserMenu onLoginClick={() => setLoginModalOpen(true)} />
          </div>
        </div>

        {/* Navigation tabs (visible on all tabs) */}
        <div style={{
          position: "absolute",
          top: "55px",
          left: "0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          zIndex: 1000
        }}>
          <div style={{ display: "flex", width: "400px", justifyContent: "space-between" }}>
            <div
              style={{
                position: "relative",
                cursor: "pointer",
                padding: "0 5px",
                textShadow: "0 1px 2px rgba(0,0,0,0.8)"
              }}
              onClick={() => window.location.href = '/categories'}
            >
              <span style={{
                fontSize: "16px",
                color: "white",
                fontWeight: "500"
              }}>
                Categories
              </span>
            </div>
            <div
              style={{
                position: "relative",
                cursor: "pointer",
                padding: "0 5px",
                textShadow: "0 1px 2px rgba(0,0,0,0.8)"
              }}
              onClick={() => window.location.href = '/'}
            >
              <span style={{
                fontSize: "16px",
                color: "white",
                fontWeight: "500"
              }}>
                For You
              </span>
            </div>
            <div
              style={{
                position: "relative",
                cursor: "pointer",
                padding: "0 5px",
                textShadow: "0 1px 2px rgba(0,0,0,0.8)"
              }}
              onClick={() => {
                if (!currentUser) {
                  setLoginModalOpen(true);
                }
              }}
            >
              <span style={{
                fontSize: "16px",
                color: "#FF6B00",
                fontWeight: "700"
              }}>
                Following
              </span>
              <div style={{
                position: "absolute",
                bottom: "-5px",
                left: "0",
                width: "100%",
                height: "3px",
                backgroundColor: "#FF6B00",
                borderRadius: "2px"
              }}></div>
            </div>
            <div
              style={{
                position: "relative",
                cursor: "pointer",
                padding: "0 5px",
                textShadow: "0 1px 2px rgba(0,0,0,0.8)"
              }}
              onClick={() => window.location.href = '/hot'}
            >
              <span style={{
                fontSize: "16px",
                color: "white",
                fontWeight: "500"
              }}>
                Hot 🔥
              </span>
            </div>
          </div>
        </div>

        <div className="following-page" style={{ paddingTop: '90px' }}>

      {loading ? (
        <div className="following-loading">
          <div className="emoji-loader">
            <span className="emoji-item">👥</span>
            <span className="emoji-item">🔥</span>
            <span className="emoji-item">📊</span>
          </div>
          <p>Loading content from people you follow...</p>
        </div>
      ) : followingUsers.length === 0 ? (
        <div className="no-following">
          <div className="no-following-content">
            <div className="no-following-icon">👥</div>
            <h2>You're not following anyone yet</h2>
            <p>Follow other users to see their hot takes and voting activity here.</p>
          </div>
        </div>
      ) : feedItems.length === 0 ? (
        <div className="no-following">
          <div className="no-following-content">
            <div className="no-following-icon">🌟</div>
            <h2>No New Content</h2>
            <p>Check back later for updates from people you follow</p>
          </div>
        </div>
      ) : (
        <>
          {/* Vertical Feed - All Items */}
          <div className="feed-items-list" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {feedItems.map((item, index) => (
              <div key={item.id} className="feed-item-card" style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '15px',
                padding: '0',
                transition: 'all 0.3s ease',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              }}>
                {item.type === 'hot-take' ? (
                  // Hot Take Card
                  <div className="following-hot-take-card" style={{ padding: '20px' }}>
                    <div className="hot-take-author">
                      <Link 
                        to={`/profile/${item.author.uid}`}
                        className="author-link"
                      >
                        <div className="author-avatar">
                          {item.author.photoURL ? (
                            <img src={item.author.photoURL} alt={item.author.displayName} />
                          ) : (
                            <span>{(item.author.displayName || 'A')[0].toUpperCase()}</span>
                          )}
                        </div>
                        <div className="author-info">
                          <div className="author-name">{item.author.displayName || item.author.username}</div>
                          <div className="hot-take-time">{formatTimeAgo(item.createdAt)}</div>
                        </div>
                      </Link>
                    </div>

                    <div className="hot-take-question">
                      <h3>{item.question}</h3>
                    </div>

                    <div className="hot-take-options">
                      <button 
                        className="hot-take-option option-a"
                        onClick={() => handleHotTakeVote(item.id, 'A')}
                      >
                        <span className="option-text">{item.optionA}</span>
                        <div className="option-stats">
                          <span className="vote-count">{item.optionAVotes || 0} votes</span>
                          {item.totalVotes > 0 && (
                            <span className="vote-percentage">
                              {Math.round(((item.optionAVotes || 0) / item.totalVotes) * 100)}%
                            </span>
                          )}
                        </div>
                      </button>
                      
                      <button 
                        className="hot-take-option option-b"
                        onClick={() => handleHotTakeVote(item.id, 'B')}
                      >
                        <span className="option-text">{item.optionB}</span>
                        <div className="option-stats">
                          <span className="vote-count">{item.optionBVotes || 0} votes</span>
                          {item.totalVotes > 0 && (
                            <span className="vote-percentage">
                              {Math.round(((item.optionBVotes || 0) / item.totalVotes) * 100)}%
                            </span>
                          )}
                        </div>
                      </button>
                    </div>

                    <div className="hot-take-interactions">
                      <span className="interaction-stat">👏 {item.claps || 0}</span>
                      <span className="interaction-stat">🍅 {item.tomatoes || 0}</span>
                      <span className="interaction-stat">💬 {item.commentCount || 0}</span>
                      <span className="interaction-stat">{item.totalVotes || 0} total votes</span>
                    </div>
                  </div>
                ) : (
                  // Activity Item - Individual Card
                  <div className="activity-item" style={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    padding: '20px'
                  }}>
                    {/* User header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px', width: '100%' }}>
                      <div className="activity-avatar">
                        {item.user.photoURL ? (
                          <img src={item.user.photoURL} alt={item.user.displayName} />
                        ) : (
                          <span>{(item.user.displayName || 'A')[0].toUpperCase()}</span>
                        )}
                      </div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <Link to={`/profile/${item.user.uid}`} className="user-link">
                            @{item.user.username || item.user.displayName}
                          </Link>
                          <span> voted "{item.chosenOptionText}"</span>
                        </div>
                        <div className="activity-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    </div>

                    {/* Question image */}
                    {item.questionImage && (
                      <div style={{ 
                        width: '100%', 
                        borderRadius: '12px', 
                        overflow: 'hidden',
                        marginBottom: '15px',
                        height: '250px',
                        position: 'relative'
                      }}>
                        <img 
                          src={item.questionImage} 
                          alt="Question" 
                          style={{ 
                            width: '100%', 
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center center',
                            display: 'block'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Question text - clickable link */}
                    <Link 
                      to={`/question/${item.questionId}`}
                      style={{
                        fontSize: '16px',
                        color: 'white',
                        lineHeight: '1.4',
                        marginBottom: '10px',
                        textDecoration: 'none',
                        display: 'block',
                        cursor: 'pointer',
                        padding: '8px',
                        borderRadius: '8px',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.05)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      "{item.question || 'Question not available'}"
                    </Link>

                    {/* Chosen option highlight */}
                    <div style={{
                      background: 'rgba(249, 115, 22, 0.1)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '14px',
                      color: '#F97316',
                      fontWeight: '600'
                    }}>
                      Voted: {item.chosenOptionText}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Show "That's all wrapped up" at the end */}
          {feedItems.length > 0 && (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: '#888',
              fontSize: '16px',
              fontStyle: 'italic'
            }}>
              That's all wrapped up
            </div>
          )}
        </>
      )}
        </div>
      </div>
    </>
  );
}

export default Following;